import { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { DEFAULT_HR_CONFIG, normalizeHrConfig, buildHrEntityPrefixes } from './hrConfigDefaults';

let hydratePromise = null;

function isHrConfigHydrated(hr) {
  return hr?._syncedFromApi === true;
}

function hydrateHrConfig(patchSettings) {
  if (!hydratePromise) {
    hydratePromise = localApiAdapter
      .get('/hr/config')
      .then((response) => {
        const raw = response?.data !== undefined ? response.data : response;
        const normalized = normalizeHrConfig(raw || {});
        syncHrConfigToSettings(normalized, patchSettings);
        return normalized;
      })
      .catch(() => {
        hydratePromise = null;
        return DEFAULT_HR_CONFIG;
      });
  }
  return hydratePromise;
}

/** Live HR module config — synced from API once, then from SettingsContext. */
export function useHrConfig() {
  const { settings, patchSettings } = useSettings();

  useEffect(() => {
    if (isHrConfigHydrated(settings?.hr)) return;
    void hydrateHrConfig(patchSettings);
  }, [settings?.hr, patchSettings]);

  return useMemo(
    () => normalizeHrConfig(settings?.hr || {}),
    [settings?.hr],
  );
}

export function syncHrConfigToSettings(config, patchSettings, existingSettings = {}) {
  const normalized = normalizeHrConfig(config);
  patchSettings({
    hr: { ...normalized, _syncedFromApi: true },
    configuration: {
      entityPrefixes: {
        ...(existingSettings?.configuration?.entityPrefixes || {}),
        ...buildHrEntityPrefixes(normalized),
      },
    },
  }, { persist: false });
  return normalized;
}

/** Sync table/cards toggle with HR config defaultView (real-time). */
export function useHrListViewMode(hrConfig) {
  const [viewMode, setViewMode] = useState(() => hrConfig?.defaultView || 'table');

  useEffect(() => {
    const mode = hrConfig?.defaultView;
    if (mode === 'grid' || mode === 'table' || mode === 'cards') {
      setViewMode(mode === 'grid' ? 'cards' : mode);
    }
  }, [hrConfig?.defaultView]);

  return [viewMode, setViewMode];
}
